import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useConfirmRetirement } from '@features/mx/operations/hooks';
import { useToast } from '@shared/hooks/use-toast';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FormField } from '@shared/services/FormServices';
import { ValueLabel } from '@features/ax/shared/ValueLabel';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { Archive, Trash2, Clock, CheckCircle2 } from 'lucide-react';
import { FIELD_CLASSES } from '@shared/utils/constants';

interface RetirementProps {
  operation?: any;
  isOperationStarted?: boolean;
  onStartOperation?: () => void;
  originFrom?: string;
}

export const Retirement: React.FC<RetirementProps> = ({
  operation,
  isOperationStarted = false,
  onStartOperation,
  originFrom,
}) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const effectiveFrom = originFrom || searchParams.get('from') || (location.state as any)?.from || undefined;
  const isFromHistory = effectiveFrom === 'history';
  const isFromPending = effectiveFrom === 'pending';

  console.log({
    isFromHistory,
    isFromPending,
  });

  const { mutate: confirmRetire, isPending: isConfirming } = useConfirmRetirement();
  const { toast } = useToast();
  // Local start trigger fallback so clicking directly starts immediately
  const [localStarted, setLocalStarted] = useState(false);

  // Workflow steps: 'details' -> 'confirm' -> 'completed'
  const [step, setStep] = useState<'details' | 'confirm' | 'completed'>('details');

  const isStarted = isOperationStarted || localStarted || operation?.statusDisplay === 'In Progress';

  const form = useForm({
    defaultValues: {
      retirementReason: '',
      disposalMethod: '',
      notes: '',
    },
  });

  const [retirementRecord, setRetirementRecord] = useState({
    retirementReason: '',
    disposalMethod: '',
    notes: '',
    resourceCode: operation?.resourceOverview?.resourceId || 'NA',
    resourceName: operation?.resourceOverview?.resourceTypeDisplay || 'NA',
    resourceType: operation?.resourceOverview?.resourceTypeDisplay || 'NA',
    resourceSubtype: operation?.resourceOverview?.resourceSubTypeDisplay || 'NA',
    serialNo: operation?.resourceOverview?.serialNo || 'NA',
  });

  let detailData = operation?.detailData || operation?.detail_data;
  if (typeof detailData === 'string') {
    try {
      detailData = JSON.parse(detailData);
    } catch (e) {
      // ignore
    }
  }
  detailData = detailData || {};

  const statusStr = String(
    operation?.statusDisplay ||
    operation?.status_display ||
    operation?.statusName ||
    operation?.status_name ||
    ''
  ).toLowerCase().trim();

  const isAlreadyRetired =
    statusStr === 'retired' ||
    statusStr === 'resource marked as retired' ||
    statusStr === 'marked as retired' ||
    statusStr === 'completed';

  const shouldShowCompleted = isFromHistory || (!isFromPending && isAlreadyRetired) || step === 'completed';

  const retirementReason =
    retirementRecord.retirementReason ||
    detailData?.retirementReason ||
    detailData?.retirement_reason ||
    detailData?.reason ||
    operation?.retirementReason ||
    operation?.retirement_reason ||
    operation?.reason ||
    'NA';

  const disposalMethod =
    retirementRecord.disposalMethod ||
    detailData?.disposalMethod ||
    detailData?.disposal_method ||
    detailData?.method ||
    operation?.disposalMethod ||
    operation?.disposal_method ||
    operation?.method ||
    'NA';

  const notes =
    retirementRecord.notes ||
    detailData?.notes ||
    detailData?.remark ||
    detailData?.remarks ||
    operation?.notes ||
    operation?.remark ||
    operation?.remarks ||
    'NA';

  const resourceCode =
    operation?.resourceOverview?.resourceId ||
    operation?.resourceOverview?.resource_id ||
    operation?.resourceCard?.resourceId ||
    operation?.resourceCode ||
    retirementRecord.resourceCode ||
    'NA';

  const resourceType =
    operation?.resourceOverview?.resourceTypeDisplay ||
    operation?.resourceOverview?.resource_type_display ||
    operation?.resourceCard?.type ||
    retirementRecord.resourceType ||
    'NA';

  const resourceSubtype =
    operation?.resourceOverview?.resourceSubTypeDisplay ||
    operation?.resourceOverview?.resource_sub_type_display ||
    operation?.resourceCard?.subType ||
    retirementRecord.resourceSubtype ||
    'NA';

  const serialNo =
    operation?.resourceOverview?.serialNo ||
    operation?.resourceOverview?.serial_no ||
    operation?.resourceCard?.serialNo ||
    operation?.serialNo ||
    retirementRecord.serialNo ||
    'NA';

  if (shouldShowCompleted) {
    return (
      <div className="space-y-5 pt-2">
        {/* Top Resource Banner */}
        <div className="bg-rose-50/40 border border-rose-100 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 font-medium">Retiring:</span>
            <span className="font-semibold text-rose-700">{resourceCode}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="bg-rose-50 text-rose-700 border-rose-200 text-xs font-medium px-2.5 py-0.5 rounded-full"
            >
              {resourceType}
            </Badge>
            {resourceSubtype !== 'NA' && (
              <Badge
                variant="outline"
                className="bg-rose-50 text-rose-700 border-rose-200 text-xs font-medium px-2.5 py-0.5 rounded-full"
              >
                {resourceSubtype}
              </Badge>
            )}
          </div>
        </div>

        {/* Card 1: Retirement Record */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 border-l-4 border-l-emerald-500 shadow-sm space-y-4">
          <h3 className="text-xs font-semibold text-gray-500 tracking-wider">
            Retirement Record
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">
            <ValueLabel label="Retirement Reason" value={retirementReason} />
            <ValueLabel label="Disposal Method" value={disposalMethod} />
          </div>

          {notes && notes !== 'NA' && (
            <div className="pt-1">
              <ValueLabel label="Remark" value={notes} />
            </div>
          )}
        </div>

        {/* Card 2: Resource Marked as Retired */}
        <div className="bg-rose-50/40 border border-rose-100 rounded-xl p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-rose-600" />
            <h4 className="text-sm font-semibold text-rose-900">
              Resource Marked as Retired
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-3 gap-x-6 pt-1">
            <ValueLabel
              label="Resource"
              value={
                <span className="font-semibold text-rose-700">
                  {resourceCode}
                </span>
              }
            />
            <ValueLabel
              label="Resource Status"
              value={
                <Badge
                  variant="outline"
                  className="bg-rose-100 text-rose-700 border-rose-200 font-medium text-xs px-2.5 py-0.5 rounded-full"
                >
                  Retired
                </Badge>
              }
            />
            <ValueLabel
              label="Serial No"
              value={serialNo}
            />
          </div>
        </div>
      </div>
    );
  }

  const fields: FormField[] = [
    {
      name: 'retirementReason',
      label: 'Retirement Reason',
      type: 'select',
      required: true,
      placeholder: 'Select reason...',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      options: [
        { label: 'End of Life', value: 'End of Life' },
        { label: 'Physical Damage', value: 'Physical Damage' },
        { label: 'Calibration Expired', value: 'Calibration Expired' },
        { label: 'Manufacturer Recalled', value: 'Manufacturer Recalled' },
        { label: 'Safety Non-Compliance', value: 'Safety Non-Compliance' },
        { label: 'Decommission Request', value: 'Decommission Request' },
        { label: 'Other', value: 'Other' },
      ],
    },
    {
      name: 'disposalMethod',
      label: 'Disposal Method',
      type: 'select',
      required: true,
      placeholder: 'Select disposal method...',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      options: [
        { label: 'Scrapped', value: 'Scrapped' },
        { label: 'Recycled', value: 'Recycled' },
        { label: 'Returned to Manufacturer', value: 'Returned to Manufacturer' },
        { label: 'Third-Party Disposal', value: 'Third-Party Disposal' },
        { label: 'Donated / Repurposed', value: 'Donated / Repurposed' },
      ],
    },
    {
      name: 'notes',
      label: 'Remark',
      type: 'textarea',
      placeholder: 'Add retirement notes...',
      classes: FIELD_CLASSES,
      className: 'col-span-2',
    },
  ];

  const handleStart = () => {
    setLocalStarted(true);
    onStartOperation?.();
  };

  // Screen 2 -> Screen 3 (Confirm Retirement)
  const onSubmit = (data: any) => {
    const payload = {
      retirement_reason: data.retirementReason,
      disposal_method: data.disposalMethod,
      notes: data.notes || '',
    };

    if (operation?.id) {
      confirmRetire({ operationId: operation.id, payload }, {
        onSuccess: () => {
          setRetirementRecord((prev) => ({
            ...prev,
            retirementReason: data.retirementReason,
            disposalMethod: data.disposalMethod,
            notes: data.notes || '',
          }));
          setStep('confirm');
          toast({ title: 'Success', description: 'Retirement details confirmed.' });
        },
        onError: (err: any) => {
          const apiMsg = err?.response?.data?.message || err?.message || 'Failed to confirm retirement.';
          toast({ title: 'Error', description: apiMsg, variant: 'destructive' });
        }
      });
    } else {
      setRetirementRecord((prev) => ({
        ...prev,
        retirementReason: data.retirementReason,
        disposalMethod: data.disposalMethod,
        notes: data.notes || '',
      }));
      setStep('confirm');
    }
  };

  // Screen 3 -> Screen 4 (Mark Resource as Retired)
  const handleMarkResourceAsRetired = () => {
    setStep('completed');
    toast({ title: 'Success', description: 'Resource marked as retired.' });
  };

  return (
    <div className="space-y-5 pt-2">
      {/* Top Resource Banner (Always Visible across all 4 screens) */}
      <div className="bg-rose-50/40 border border-rose-100 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 font-medium">Retiring:</span>
          <span className="font-semibold text-rose-700">{retirementRecord.resourceCode}</span>
          {/* <span className="text-gray-600">{retirementRecord.resourceName}</span> */}
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="bg-rose-50 text-rose-700 border-rose-200 text-xs font-medium px-2.5 py-0.5 rounded-full"
          >
            {retirementRecord.resourceType}
          </Badge>
          <Badge
            variant="outline"
            className="bg-rose-50 text-rose-700 border-rose-200 text-xs font-medium px-2.5 py-0.5 rounded-full"
          >
            {retirementRecord.resourceSubtype}
          </Badge>
        </div>
      </div>

      {/* Screen 1: Operation Not Started */}
      {!isStarted ? (
        <div className="bg-amber-50/50 border border-amber-200/70 rounded-xl p-10 text-center space-y-2">
          <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-2">
            <Clock className="w-4 h-4" />
          </div>
          <h4 className="text-sm font-semibold text-amber-900">Operation Not Started</h4>
          <p className="text-xs text-amber-700">
            Start the operation to begin the retirement process.
          </p>
        </div>
      ) : step === 'details' ? (
        /* Screen 2: Retirement Details Form (After Start Operation) */
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
          {/* Header with Icon */}
          <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
            <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
              <Trash2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Retirement Details</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Record disposal details to confirm full resource retirement
              </p>
            </div>
          </div>

          {/* DynamicForm */}
          <DynamicForm
            form={form}
            fields={fields}
            onSubmit={onSubmit}
            gridColumns={2}
            config={{ showSubmitButton: false, showResetButton: false }}
            renderCustomFooter={() => <></>}
          />

          {/* Action Button */}
          <Button
            type="button"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isConfirming}
            className="w-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2 py-3 rounded-xl shadow-sm font-medium text-sm"
          >
            <Archive className="w-4 h-4" />
            <span>{isConfirming ? 'Confirming...' : 'Confirm Retirement'}</span>
          </Button>
        </div>
      ) : step === 'confirm' ? (
        /* Screen 3: Complete Retirement (After Confirm Retirement) */
        <div className="space-y-5">
          {/* Card 1: Retirement Record */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 border-l-4 border-l-emerald-500 shadow-sm space-y-4">
            <h3 className="text-xs font-semibold text-gray-500 tracking-wider">
              Retirement Record
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">
              <ValueLabel
                label="Retirement Reason"
                value={retirementRecord.retirementReason}
              />
              <ValueLabel
                label="Disposal Method"
                value={retirementRecord.disposalMethod}
              />
            </div>

            {retirementRecord.notes && (
              <div className="pt-1">
                <ValueLabel label="Remark" value={retirementRecord.notes} />
              </div>
            )}
          </div>

          {/* Card 2: Complete Retirement */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
              <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Complete Retirement</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Mark this resource as retired in the system
                </p>
              </div>
            </div>

            {/* Resource Status Transition Bar */}
            <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100/80 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-rose-700">{retirementRecord.resourceCode}</span>
                <Badge
                  variant="outline"
                  className="bg-rose-100/80 text-rose-700 border-rose-200 text-[11px] font-medium px-2 py-0.5 rounded-full"
                >
                  {retirementRecord.resourceType}
                </Badge>
                <Badge
                  variant="outline"
                  className="bg-rose-100/80 text-rose-700 border-rose-200 text-[11px] font-medium px-2 py-0.5 rounded-full"
                >
                  {retirementRecord.resourceSubtype}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500">Resource Status will be set to</span>
                <Badge
                  variant="outline"
                  className="bg-rose-100/90 text-rose-700 border-rose-200 font-medium text-xs px-2.5 py-0.5 rounded-full"
                >
                  Retired
                </Badge>
              </div>
            </div>

            {/* Action Button */}
            <Button
              type="button"
              onClick={handleMarkResourceAsRetired}
              className="w-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2 py-3 rounded-xl shadow-sm transition-all font-medium text-sm"
            >
              <Archive className="w-4 h-4" />
              <span>Mark Resource as Retired</span>
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
export default Retirement;
